// src/components/Footer.js
// ──────────────────────────────────────────────────────────────────────────
// Institutional footer (blue + lemon) with MTI's real contact details.
// Exports both `Footer` (named + default) and a small `CallToAction` for
// backward compatibility with any older imports.
// ──────────────────────────────────────────────────────────────────────────

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useSiteContent } from '../context/SiteContentContext';
import theme from '../theme/theme';
import logo from '../assets/star.png';

const Wrap = styled.footer`
  background: ${theme.color.blueDeep};
  color: rgba(255,255,255,0.78);
  font-size: 0.92rem;
`;

const Top = styled.div`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 4.5rem ${theme.layout.gutter} 3rem;
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1.3fr;
  gap: 2.5rem;
  @media (max-width: 880px) { grid-template-columns: 1fr 1fr; gap: 2.5rem 2rem; }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const Brand = styled.div`
  .logo { height: 66px; width: 66px; object-fit: cover; border-radius: 50%; border: 1px solid rgba(255,255,255,0.18); margin-bottom: 1rem; display: block; }
  .mark { font-family: ${theme.font.display}; font-weight: 800; font-size: 1.2rem; color: #fff; letter-spacing: -0.01em; }
  .mark i { font-style: normal; color: ${theme.color.lemon}; }
  .tag { margin-top: 0.9rem; max-width: 30ch; font-size: 0.9rem; color: rgba(255,255,255,0.7); }
  .rule { width: 40px; height: 2px; background: ${theme.gradient.rule}; margin-top: 1.2rem; }
`;

const Col = styled.div`
  h4 { font-family: ${theme.font.body}; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: ${theme.color.lemon}; margin-bottom: 1rem; }
  a, p { display: block; color: rgba(255,255,255,0.78); margin-bottom: 0.6rem; line-height: 1.5; }
  a:hover { color: #fff; }
  p { margin-bottom: 0.9rem; }
`;

const Bottom = styled.div`
  border-top: 1px solid rgba(255,255,255,0.12);
  .inner {
    max-width: ${theme.layout.maxWidth}; margin: 0 auto;
    padding: 1.4rem ${theme.layout.gutter};
    display: flex; justify-content: space-between; align-items: center; gap: 1rem;
    font-size: 0.82rem; color: rgba(255,255,255,0.6); flex-wrap: wrap;
  }
  .motto { color: ${theme.color.lemon}; font-weight: 500; }
`;

export const Footer = () => {
  const contact = useSiteContent('contact') || theme.info;
  const year = new Date().getFullYear();

  return (
    <Wrap>
      <Top>
        <Brand>
          <img className="logo" src={logo} alt="CGA Mbala Training Institute" />
          <div className="mark">CGA Mbala Training Institute</div>
          <p className="tag">Enhancing and equipping the nation with skills for the future.</p>
          <div className="rule" />
        </Brand>

        <Col>
          <h4>Explore</h4>
          <Link to="/programs">Programmes</Link>
          <Link to="/admissions">Admissions</Link>
          <Link to="/about">About</Link>
          <Link to="/news">News</Link>
          <Link to="/contact">Contact</Link>
        </Col>

        <Col>
          <h4>Departments</h4>
          <Link to="/programs">Agriculture</Link>
          <Link to="/programs">Engineering</Link>
          <Link to="/programs">Hospitality</Link>
          <Link to="/programs">ICT</Link>
        </Col>

        <Col>
          <h4>Visit / Contact</h4>
          {contact?.address && <p>{contact.address}</p>}
          {contact?.postal && <p>{contact.postal}</p>}
          {contact?.email && <a href={`mailto:${contact.email}`}>{contact.email}</a>}
          {contact?.phone && <a href={`tel:${contact.phone.replace(/\s/g, '')}`}>{contact.phone}</a>}
        </Col>
      </Top>

      <Bottom>
        <div className="inner">
          <span>© {year} Mbala Training Institute. All rights reserved.</span>
          <span className="motto">Building You From Scratch</span>
        </div>
      </Bottom>
    </Wrap>
  );
};

// Backward-compat: a minimal CallToAction so old imports don't break.
export const CallToAction = () => null;

export default Footer;
